import { useCallback, useEffect, useState } from "react";
import { Fingerprint, Loader2, Trash2 } from "lucide-react";
import { errMsg } from "@/lib/api";
import { addPasskey, cancelled, listPasskeys, passkeysSupported, removePasskey } from "@/lib/passkeys";
import { Button } from "@/components/ui/button";

const when = (stamp) => (stamp ? new Date(stamp).toLocaleDateString() : "not used yet");

/** Add or remove the Face ID / fingerprint unlock for whoever is signed in. */
export const PasskeyPanel = ({ testId = "passkey-panel" }) => {
  const [keys, setKeys] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      setKeys(await listPasskeys());
    } catch (e) {
      setError(errMsg(e));
      setKeys([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await addPasskey(navigator.platform || "This device");
      setNotice("Done — next time, just press Unlock with Face ID.");
      await load();
    } catch (e) {
      if (!cancelled(e)) setError(e?.name === "InvalidStateError"
        ? "This device already has a passkey for your account."
        : errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const drop = async (id) => {
    setBusy(true);
    setError("");
    try {
      await removePasskey(id);
      setNotice("Passkey removed.");
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!passkeysSupported()) {
    return (
      <p data-testid={`${testId}-unsupported`} className="text-sm text-slate-500">
        This browser cannot store a passkey. Open the app on your phone to set up Face ID unlock.
      </p>
    );
  }

  return (
    <div data-testid={testId} className="space-y-4">
      <p className="text-sm text-slate-400">
        Unlock with Face ID, Touch ID or your fingerprint instead of typing a password. Your password
        keeps working, so you can never be locked out.
      </p>
      {keys === null ? <Loader2 className="h-4 w-4 animate-spin text-gold" /> : (
        <div data-testid={`${testId}-list`} className="space-y-2">
          {!keys.length && (
            <p data-testid={`${testId}-empty`} className="text-sm text-slate-500">
              No passkey on this account yet.
            </p>
          )}
          {keys.map((key) => (
            <div key={key.id} data-testid={`${testId}-row-${key.id}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-ink-600 bg-ink-900 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm text-slate-200">{key.label}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  last used {when(key.last_used_at)}{key.backed_up ? " · synced" : ""}
                </p>
              </div>
              <Button data-testid={`${testId}-delete-${key.id}`} variant="ghost" size="sm"
                disabled={busy} onClick={() => drop(key.id)}
                className="text-slate-400 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      {error && <p data-testid={`${testId}-error`} className="text-sm text-red-400">{error}</p>}
      {notice && <p data-testid={`${testId}-notice`} className="text-sm text-emerald-400">{notice}</p>}
      <Button data-testid={`${testId}-add`} disabled={busy} onClick={add}>
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          : <Fingerprint className="mr-2 h-4 w-4" />}
        Add a passkey to this device
      </Button>
    </div>
  );
};
